import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../../chart/bodygraph/gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../../chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { CommunityContentAccessService } from '../community-content-access.service';
import { HdLineCompanionPanelComponent } from './hd-line-companion-panel.component';
import {
  HD_LINE_COMPANION_PANEL_KEY,
  HdLineCompanionPanelData,
  HdLineCompanionTarget,
} from './hd-line-companion-panel.models';

/**
 * Opent het Line Companion-paneel (community-content). Zelfde patroon als
 * `HdFearsPanelService`: één paneel (key `line-companion`), dock-/dialoogkeuze
 * uit de globale `gateInfoOpenBehavior`-setting die chart-pagina's live
 * doorgeven, en `replaceData` met een verse referentie zodat een al open
 * paneel opnieuw navigeert/scrollt (ook naar dezelfde lijn). Op pagina's
 * zonder dock-zone (cheat sheets, transit-pagina) floatt het paneel als
 * dialoog — het gedrag dat die pagina's al kennen.
 *
 * Alle opens zijn no-ops zonder toegang (feature + opt-in-setting), zodat een
 * vergeten guard bij een entry-point nooit content lekt.
 */
@Injectable({ providedIn: 'root' })
export class HdLineCompanionPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly access = inject(CommunityContentAccessService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  /** Opent het paneel op het poortenoverzicht. */
  openOverview(origin?: { x: number; y: number }): void {
    this.open({ view: 'overview' }, origin);
  }

  /**
   * Opent het paneel op een poortpagina; met `focusLine` scrollt het paneel
   * automatisch naar de betreffende lijnsectie.
   */
  openGate(gate: number, options?: { focusLine?: number | null; origin?: { x: number; y: number } }): void {
    this.open({ view: 'gate', gate, focusLine: options?.focusLine ?? null }, options?.origin);
  }

  private open(target: HdLineCompanionTarget, origin?: { x: number; y: number }): void {
    if (!this.access.lineCompanionVisible()) {
      return;
    }
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    const data: HdLineCompanionPanelData = { target };

    const ref = this.floatingPanels.openOrFocus<HdLineCompanionPanelComponent, HdLineCompanionPanelData>({
      key: HD_LINE_COMPANION_PANEL_KEY,
      component: HdLineCompanionPanelComponent,
      data,
      dockTab: { label: 'LC', icon: 'book-open-lines' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null ? { preferred: origin, hint: 'prefer-right-of-anchor' } : 'center',
    });
    // Verse referentie: ook een al open paneel navigeert/scrollt opnieuw.
    ref.replaceData({ ...data });
  }
}
